import { t } from "ttag";

import type { ServerCmsTypesFilterFacet as FilterFacet } from "@reactivated";

import type { SelectedFilters } from "./filterProducts";
import { getQualifierLabel, getTitleQualification } from "./utils";

interface CategorySEOOverrides {
    seo_title: string;
    search_description: string;
    robots: string;
    canonicalUrl: string;
    ogTitle: string;
    ogDescription: string;
    ogUrl: string;
}

/**
 * Compute SEO overrides for a category page based on the active filter state.
 *
 * - Title-qualifying (single `use_in_page_title` filter): unique title,
 *   description, `index, follow`, self-referencing canonical, and OG tags.
 * - Non-qualifying filters active: `noindex, follow`, canonical to base URL,
 *   OG tags from base page values.
 * - No filters: OG tags from base page values, everything else defaults.
 */
export const getCategorySEO = ({
    filterFacets,
    selectedFilters,
    categoryTitle,
    requestUrl,
    rootTitle,
}: {
    filterFacets: FilterFacet[];
    selectedFilters: SelectedFilters;
    categoryTitle: string;
    requestUrl: string;
    rootTitle: string;
}): Partial<CategorySEOOverrides> => {
    const url = new URL(requestUrl);
    const baseUrl = `${url.origin}${url.pathname}`;

    const hasActiveFilters = Object.values(selectedFilters).flat().length > 0;
    const qualification = hasActiveFilters
        ? getTitleQualification(filterFacets, selectedFilters)
        : null;

    const seoTitle = qualification
        ? `${getQualifierLabel(qualification)} ${categoryTitle}`
        : categoryTitle;
    const description = t`Explore SKS ${seoTitle}. Browse specs, finishes, and features for your kitchen.`;
    const baseOG: Partial<CategorySEOOverrides> = {
        search_description: description,
        ogTitle: [seoTitle, rootTitle].join(" | "),
        ogDescription: description,
        ogUrl: baseUrl,
    };

    if (!hasActiveFilters) return { ...baseOG, canonicalUrl: baseUrl };

    if (qualification) {
        const qs = new URLSearchParams([
            [qualification.facetSlug, qualification.valueSlug],
        ]);
        const canonicalUrl = `${baseUrl}?${qs}`;

        return {
            ...baseOG,
            seo_title: seoTitle,
            robots: "index, follow",
            canonicalUrl,
            ogUrl: canonicalUrl,
        };
    }

    return {
        ...baseOG,
        robots: "noindex, follow",
        canonicalUrl: baseUrl,
    };
};
